import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  TableSortLabel,
  Grid,
  useMediaQuery,
  useTheme,
  Menu,
  ListItemText,
  Autocomplete
} from '@mui/material';
import { rawItemService, rawItemTransactionService, RawItem, RawItemTransaction } from '../services/rawItemService';
import { usePermissions } from '../hooks/usePermissions';
import { API_BASE_URL } from '../constants/config';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface RawItemWithTotalInventory extends RawItem {
  totalInventoryAdded: number;
  totalValue: number;
  perUnitPrice: number;
}

interface Vendor {
  id: number;
  name: string;
  status: string;
}

interface User {
  id: number;
  username: string;
  role: {
    id: number;
    name: string;
  };
}

interface InventoryFormState {
  rawItemId: string;
  quantity: string;
  totalAmount: string;
  transactionDate: string;
  notes: string;
  vendor: string;
  paymentMethod: string;
}

const PAYMENT_METHOD_OPTIONS = [
  'Online transfer from HC',
  'Cheque payment from HC',
  'Online Transfer from AZ',
  'Metro Card'
];

const parseTransactionDateToInput = (transactionDate: string): string => {
  if (!transactionDate) {
    return new Date().toISOString().split('T')[0];
  }
  return transactionDate.split(' ')[0].split('T')[0];
};

// Utility function to convert UTC date string to browser's local timezone
const convertUTCToLocalTime = (utcDateString: string): string => {
  try {
    // The server is in UTC timezone, so database dates are in UTC
    // Format: "yyyy-MM-dd HH:mm:ss" (UTC time)
    let dateString = utcDateString;
    
    // Convert space to 'T' and add 'Z' to make it explicit UTC
    if (dateString.includes(' ')) {
      dateString = dateString.replace(' ', 'T') + 'Z';
    } else if (dateString.includes('T') && !dateString.includes('Z')) {
      dateString = dateString + 'Z';
    }
    
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', utcDateString);
      return utcDateString;
    }
    
    // Return formatted date in local timezone
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('Error converting date:', utcDateString, error);
    return utcDateString;
  }
};

const RawMaterial: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const role = localStorage.getItem('role') || '';
  const { hasPermission } = usePermissions(role);
  
  const [rawItems, setRawItems] = useState<RawItemWithTotalInventory[]>([]);
  const [rawItemTransactions, setRawItemTransactions] = useState<RawItemTransaction[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]); // Enabled vendors for Add Inventory
  const [allVendors, setAllVendors] = useState<Vendor[]>([]); // All vendors for Vendor Report
  const [users, setUsers] = useState<User[]>([]); // Users for Vendor Report
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Search and sort state
  const [orderBy, setOrderBy] = useState<keyof RawItemWithTotalInventory>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  // Add Raw Item Dialog
  const [addRawItemDialogOpen, setAddRawItemDialogOpen] = useState(false);
  const [newRawItem, setNewRawItem] = useState({
    name: '',
    quantityUnit: '',
    category: ''
  });

  // Add Raw Item Inventory Dialog
  const [addInventoryDialogOpen, setAddInventoryDialogOpen] = useState(false);
  const [newInventory, setNewInventory] = useState({
    rawItemId: '',
    quantity: '',
    totalAmount: '',
    transactionDate: new Date().toISOString().split('T')[0], // Default to today
    notes: '',
    vendor: '',
    paymentMethod: ''
  });

  // Edit Raw Item Dialog
  const [editRawItemDialogOpen, setEditRawItemDialogOpen] = useState(false);
  const [editingRawItem, setEditingRawItem] = useState<RawItem | null>(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);

  // Date range filter
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });

  // Details Dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedItemForDetails, setSelectedItemForDetails] = useState<RawItemWithTotalInventory | null>(null);
  const [itemInventoryEntries, setItemInventoryEntries] = useState<RawItemTransaction[]>([]);

  // Vendor Inventory Report Dialog
  const [vendorReportDialogOpen, setVendorReportDialogOpen] = useState(false);
  const [vendorReportFilters, setVendorReportFilters] = useState({
    vendor: null as Vendor | null,
    user: null as User | null,
    startDate: '',
    endDate: ''
  });
  const [vendorReportTransactions, setVendorReportTransactions] = useState<RawItemTransaction[]>([]);

  // Edit / Delete inventory entry (Administrator only)
  const [editInventoryDialogOpen, setEditInventoryDialogOpen] = useState(false);
  const [editingInventoryEntry, setEditingInventoryEntry] = useState<RawItemTransaction | null>(null);
  const [editInventory, setEditInventory] = useState<InventoryFormState>({
    rawItemId: '',
    quantity: '',
    totalAmount: '',
    transactionDate: new Date().toISOString().split('T')[0],
    notes: '',
    vendor: '',
    paymentMethod: ''
  });
  const [deleteInventoryDialogOpen, setDeleteInventoryDialogOpen] = useState(false);
  const [deletingInventoryEntry, setDeletingInventoryEntry] = useState<RawItemTransaction | null>(null);

  useEffect(() => {
    loadData();
    fetchEnabledVendors();
    // Only fetch all vendors if user has permission to view vendor report
    if (hasPermission('view-vendor-inventory-report')) {
      fetchAllVendors();
    }
  }, [dateRange]);

  const fetchEnabledVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/vendors/enabled`, {
        headers: { Authorization: token }
      });
      setVendors(response.data);
    } catch (err: any) {
      console.error('Error fetching enabled vendors:', err);
    }
  };

  const fetchAllVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/vendors`, {
        headers: { Authorization: token }
      });
      setAllVendors(response.data);
    } catch (err: any) {
      console.error('Error fetching all vendors:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: token }
      });
      // Filter users to only show Factory Raw Inventory Adder or Administrator
      const filteredUsers = response.data.filter((user: User) => 
        user.role?.name === 'Factory Raw Inventory Adder' || user.role?.name === 'Administrator'
      );
      setUsers(filteredUsers);
    } catch (err: any) {
      console.error('Error fetching users:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [items, transactions] = await Promise.all([
        rawItemService.getAllRawItems(),
        rawItemTransactionService.getAllRawItemTransactions()
      ]);
      
      // Filter transactions by date range if specified
      let filteredTransactions = transactions;
      if (dateRange.start || dateRange.end) {
        filteredTransactions = transactions.filter(t => {
          const transactionDate = new Date(t.transactionDate);
          
          // If only start date is selected
          if (dateRange.start && !dateRange.end) {
            const startDate = new Date(dateRange.start);
            startDate.setHours(0, 0, 0, 0); // Set to beginning of day
            return transactionDate >= startDate;
          }
          
          // If only end date is selected
          if (!dateRange.start && dateRange.end) {
            const endDate = new Date(dateRange.end);
            endDate.setHours(23, 59, 59, 999); // Set to end of day
            return transactionDate <= endDate;
          }
          
          // If both dates are selected
          if (dateRange.start && dateRange.end) {
            const startDate = new Date(dateRange.start);
            startDate.setHours(0, 0, 0, 0); // Set to beginning of day
            const endDate = new Date(dateRange.end);
            endDate.setHours(23, 59, 59, 999); // Set to end of day
            return transactionDate >= startDate && transactionDate <= endDate;
          }
          
          return true; // No date filter
        });
      }
      
      // Calculate total inventory, total value, and per unit price for each raw item
      const itemsWithTotalInventory = items.map(item => {
        const itemTransactions = filteredTransactions.filter(t => t.rawItemId === item.id && t.transactionType === 'ADDTOFACTORY');
        const totalInventoryAdded = itemTransactions.reduce((sum, t) => sum + t.quantity, 0);
        const totalValue = itemTransactions.reduce((sum, t) => sum + t.totalAmount, 0);
        const perUnitPrice = totalInventoryAdded > 0 ? totalValue / totalInventoryAdded : 0;
        
        return {
          ...item,
          totalInventoryAdded,
          totalValue,
          perUnitPrice
        };
      });
      
      // Filter out items with zero inventory if any date filter is specified
      const finalItems = (dateRange.start || dateRange.end)
        ? itemsWithTotalInventory.filter(item => item.totalInventoryAdded > 0)
        : itemsWithTotalInventory;
      
      setRawItems(finalItems);
      setRawItemTransactions(transactions);
    } catch (error) {
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };


  // Sort handler
  const handleSort = (property: keyof RawItemWithTotalInventory) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter and sort function
  const getFilteredAndSortedRawItems = () => {
    let filtered = rawItems;
    
    
    // Apply sorting
    return filtered.sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return order === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });
  };

  // Calculate totals
  const calculateTotals = (data: RawItemWithTotalInventory[]) => {
    return data.reduce((acc, item) => ({
      totalInventoryAdded: acc.totalInventoryAdded + item.totalInventoryAdded,
      totalValue: acc.totalValue + item.totalValue
    }), {
      totalInventoryAdded: 0,
      totalValue: 0
    });
  };

  // PDF export function
  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      const filteredData = getFilteredAndSortedRawItems();
      const totals = calculateTotals(filteredData);
      const currentDate = new Date().toLocaleString();

      // Add title and metadata
      doc.setFontSize(16);
      doc.text('Raw Material Inventory Report', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated on: ${currentDate}`, 14, 22);

      // Prepare table data
      const tableData = filteredData.map(item => [
        item.name,
        item.category,
        item.quantityUnit,
        item.totalInventoryAdded.toFixed(2),
        `PKR ${item.perUnitPrice.toFixed(2)}`,
        `PKR ${item.totalValue.toFixed(2)}`
      ]);

      // Add total row
      tableData.push([
        'Total',
        '',
        '',
        totals.totalInventoryAdded.toFixed(2),
        '',
        `PKR ${totals.totalValue.toFixed(2)}`
      ]);

      // Add table to PDF
      autoTable(doc, {
        head: [['Name', 'Category', 'Quantity Unit', 'Total Inventory Added', 'Per Unit Price', 'Total Value']],
        body: tableData,
        startY: 29,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] }
      });

      doc.save('raw-material-inventory.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showSnackbar('Failed to generate PDF report', 'error');
    }
  };

  const handleAddRawItem = async () => {
    try {
      const rawItem = await rawItemService.createRawItem({
        name: newRawItem.name,
        quantityUnit: newRawItem.quantityUnit,
        category: newRawItem.category
      });
      
      // Add the new item with 0 total inventory and value
      const newItemWithTotal: RawItemWithTotalInventory = {
        ...rawItem,
        totalInventoryAdded: 0,
        totalValue: 0,
        perUnitPrice: 0
      };
      
      setRawItems([...rawItems, newItemWithTotal]);
      setAddRawItemDialogOpen(false);
      setNewRawItem({ name: '', quantityUnit: '', category: '' });
      showSnackbar('Raw item added successfully', 'success');
    } catch (error) {
      showSnackbar('Error adding raw item', 'error');
    }
  };

  const validateInventoryForm = (form: InventoryFormState): string | null => {
    if (!form.rawItemId) {
      return 'Please select a raw item';
    }
    const quantity = parseFloat(form.quantity);
    if (!form.quantity.trim() || !Number.isFinite(quantity) || quantity <= 0) {
      return 'Quantity must be greater than zero';
    }
    const totalAmount = parseFloat(form.totalAmount);
    if (!form.totalAmount.trim() || !Number.isFinite(totalAmount) || totalAmount <= 0) {
      return 'Total amount must be greater than zero';
    }
    if (!form.transactionDate) {
      return 'Transaction date is required';
    }
    if (!form.vendor || form.vendor.trim() === '') {
      return 'Please select a vendor';
    }
    if (!form.paymentMethod || form.paymentMethod.trim() === '') {
      return 'Please select a payment method';
    }
    return null;
  };

  const getItemInventoryEntries = (item: RawItemWithTotalInventory, transactions: RawItemTransaction[]) => {
    let itemTransactions = transactions.filter(t =>
      t.rawItemId === item.id && t.transactionType === 'ADDTOFACTORY'
    );

    if (dateRange.start || dateRange.end) {
      itemTransactions = itemTransactions.filter(t => {
        const transactionDate = new Date(t.transactionDate);

        if (dateRange.start && !dateRange.end) {
          const startDate = new Date(dateRange.start);
          startDate.setHours(0, 0, 0, 0);
          return transactionDate >= startDate;
        }

        if (!dateRange.start && dateRange.end) {
          const endDate = new Date(dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          return transactionDate <= endDate;
        }

        if (dateRange.start && dateRange.end) {
          const startDate = new Date(dateRange.start);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          return transactionDate >= startDate && transactionDate <= endDate;
        }

        return true;
      });
    }

    return itemTransactions.sort((a, b) =>
      new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime()
    );
  };

  const refreshAfterInventoryChange = async (rawItemId: number) => {
    const [items, transactions] = await Promise.all([
      rawItemService.getAllRawItems(),
      rawItemTransactionService.getAllRawItemTransactions()
    ]);

    let filteredTransactions = transactions;
    if (dateRange.start || dateRange.end) {
      filteredTransactions = transactions.filter(t => {
        const transactionDate = new Date(t.transactionDate);

        if (dateRange.start && !dateRange.end) {
          const startDate = new Date(dateRange.start);
          startDate.setHours(0, 0, 0, 0);
          return transactionDate >= startDate;
        }

        if (!dateRange.start && dateRange.end) {
          const endDate = new Date(dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          return transactionDate <= endDate;
        }

        if (dateRange.start && dateRange.end) {
          const startDate = new Date(dateRange.start);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          return transactionDate >= startDate && transactionDate <= endDate;
        }

        return true;
      });
    }

    const itemsWithTotalInventory = items.map(item => {
      const itemTransactions = filteredTransactions.filter(t => t.rawItemId === item.id && t.transactionType === 'ADDTOFACTORY');
      const totalInventoryAdded = itemTransactions.reduce((sum, t) => sum + t.quantity, 0);
      const totalValue = itemTransactions.reduce((sum, t) => sum + t.totalAmount, 0);
      const perUnitPrice = totalInventoryAdded > 0 ? totalValue / totalInventoryAdded : 0;

      return {
        ...item,
        totalInventoryAdded,
        totalValue,
        perUnitPrice
      };
    });

    const finalItems = (dateRange.start || dateRange.end)
      ? itemsWithTotalInventory.filter(item => item.totalInventoryAdded > 0)
      : itemsWithTotalInventory;

    setRawItems(finalItems);
    setRawItemTransactions(transactions);

    if (detailsDialogOpen && selectedItemForDetails) {
      const updatedItem = finalItems.find(item => item.id === rawItemId);
      if (updatedItem) {
        setSelectedItemForDetails(updatedItem);
        setItemInventoryEntries(getItemInventoryEntries(updatedItem, transactions));
      }
    }
  };

  const handleAddInventory = async () => {
    const validationError = validateInventoryForm(newInventory);
    if (validationError) {
      showSnackbar(validationError, 'error');
      return;
    }

    try {
      const transaction = await rawItemTransactionService.createRawItemTransaction({
        rawItemId: parseInt(newInventory.rawItemId),
        transactionType: 'ADDTOFACTORY',
        quantity: parseFloat(newInventory.quantity),
        totalAmount: parseFloat(newInventory.totalAmount),
        notes: newInventory.notes,
        transactionDate: newInventory.transactionDate + ' 00:00:00', // Add time to date
        vendor: newInventory.vendor,
        paymentMethod: newInventory.paymentMethod
      });
      
      setRawItemTransactions([transaction, ...rawItemTransactions]);
      
      // Update the total inventory and value for the selected raw item
      setRawItems(rawItems.map(item => {
        if (item.id === parseInt(newInventory.rawItemId)) {
          const newTotalInventory = item.totalInventoryAdded + parseFloat(newInventory.quantity);
          const newTotalValue = item.totalValue + parseFloat(newInventory.totalAmount);
          const newPerUnitPrice = newTotalInventory > 0 ? newTotalValue / newTotalInventory : 0;
          
          return {
            ...item,
            totalInventoryAdded: newTotalInventory,
            totalValue: newTotalValue,
            perUnitPrice: newPerUnitPrice
          };
        }
        return item;
      }));
      
      setAddInventoryDialogOpen(false);
      setNewInventory({ 
        rawItemId: '', 
        quantity: '', 
        totalAmount: '', 
        transactionDate: new Date().toISOString().split('T')[0],
        notes: '',
        vendor: '',
        paymentMethod: ''
      });
      showSnackbar('Inventory added successfully', 'success');
    } catch (error) {
      showSnackbar('Error adding inventory', 'error');
    }
  };

  const handleEditRawItem = async () => {
    if (!editingRawItem) return;
    try {
      const updatedItem = await rawItemService.updateRawItem(editingRawItem.id, {
        name: editingRawItem.name,
        quantityUnit: editingRawItem.quantityUnit,
        category: editingRawItem.category
      });
      
      setRawItems(rawItems.map(item => 
        item.id === updatedItem.id 
          ? { ...item, ...updatedItem }
          : item
      ));
      
      setEditRawItemDialogOpen(false);
      setEditingRawItem(null);
      showSnackbar('Raw item updated successfully', 'success');
    } catch (error) {
      showSnackbar('Error updating raw item', 'error');
    }
  };

  const handleShowDetails = (item: RawItemWithTotalInventory) => {
    setSelectedItemForDetails(item);
    setItemInventoryEntries(getItemInventoryEntries(item, rawItemTransactions));
    setDetailsDialogOpen(true);
  };

  const handleOpenEditInventory = (entry: RawItemTransaction) => {
    setEditingInventoryEntry(entry);
    setEditInventory({
      rawItemId: entry.rawItemId.toString(),
      quantity: entry.quantity.toString(),
      totalAmount: entry.totalAmount.toString(),
      transactionDate: parseTransactionDateToInput(entry.transactionDate),
      notes: entry.notes || '',
      vendor: entry.vendor || '',
      paymentMethod: entry.paymentMethod || ''
    });
    setEditInventoryDialogOpen(true);
  };

  const handleUpdateInventory = async () => {
    if (!editingInventoryEntry) return;

    const validationError = validateInventoryForm(editInventory);
    if (validationError) {
      showSnackbar(validationError, 'error');
      return;
    }

    try {
      await rawItemTransactionService.updateRawItemTransaction(editingInventoryEntry.id, {
        rawItemId: parseInt(editInventory.rawItemId),
        transactionType: 'ADDTOFACTORY',
        quantity: parseFloat(editInventory.quantity),
        totalAmount: parseFloat(editInventory.totalAmount),
        transactionDate: editInventory.transactionDate + ' 00:00:00',
        notes: editInventory.notes,
        vendor: editInventory.vendor,
        paymentMethod: editInventory.paymentMethod
      });

      await refreshAfterInventoryChange(parseInt(editInventory.rawItemId));
      setEditInventoryDialogOpen(false);
      setEditingInventoryEntry(null);
      showSnackbar('Inventory entry updated successfully', 'success');
    } catch (error: any) {
      const message = error?.response?.data || 'Error updating inventory entry';
      showSnackbar(typeof message === 'string' ? message : 'Error updating inventory entry', 'error');
    }
  };

  const handleOpenDeleteInventory = (entry: RawItemTransaction) => {
    setDeletingInventoryEntry(entry);
    setDeleteInventoryDialogOpen(true);
  };

  const handleDeleteInventory = async () => {
    if (!deletingInventoryEntry) return;

    try {
      await rawItemTransactionService.deleteRawItemTransaction(deletingInventoryEntry.id);
      await refreshAfterInventoryChange(deletingInventoryEntry.rawItemId);
      setDeleteInventoryDialogOpen(false);
      setDeletingInventoryEntry(null);
      showSnackbar('Inventory entry deleted successfully', 'success');
    } catch (error: any) {
      const message = error?.response?.data || 'Error deleting inventory entry';
      showSnackbar(typeof message === 'string' ? message : 'Error deleting inventory entry', 'error');
    }
  };

  const getQuantityUnitOptions = () => [
    'KG', 'L', 'GRAM', 'COUNT'
  ];

  const isPositiveNumber = (value: string): boolean => {
    const n = parseFloat(value);
    return value.trim() !== '' && Number.isFinite(n) && n > 0;
  };

  const isInventoryFormComplete = (form: InventoryFormState): boolean => {
    return isPositiveNumber(form.quantity)
      && isPositiveNumber(form.totalAmount)
      && form.vendor.trim() !== ''
      && form.paymentMethod.trim() !== ''
      && form.rawItemId !== ''
      && form.transactionDate !== '';
  };

  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleActionSelect = (action: string) => {
    handleActionsMenuClose();
    switch (action) {
      case 'add-raw-item':
        setAddRawItemDialogOpen(true);
        break;
      case 'add-inventory':
        setAddInventoryDialogOpen(true);
        break;
      case 'vendor-report':
        handleVendorReportDialogOpen();
        break;
      case 'export':
        exportToPDF();
        break;
    }
  };

  const handleVendorReportSearch = () => {
    // At least one filter must be provided
    if (!vendorReportFilters.vendor && !vendorReportFilters.user && !vendorReportFilters.startDate && !vendorReportFilters.endDate) {
      return;
    }

    // Filter transactions by vendor, user, and/or date range
    const filtered = rawItemTransactions.filter(t => {
      // Filter by vendor if provided
      if (vendorReportFilters.vendor) {
        if (t.vendor !== vendorReportFilters.vendor.name) {
          return false;
        }
      }

      // Filter by user if provided
      if (vendorReportFilters.user) {
        if (t.userId !== vendorReportFilters.user.id) {
          return false;
        }
      }

      // Filter by date range if provided
      if (vendorReportFilters.startDate || vendorReportFilters.endDate) {
        const transactionDate = new Date(t.transactionDate);
        
        // Check start date if provided
        if (vendorReportFilters.startDate) {
          const startDate = new Date(vendorReportFilters.startDate);
          startDate.setHours(0, 0, 0, 0);
          if (transactionDate < startDate) {
            return false;
          }
        }
        
        // Check end date if provided
        if (vendorReportFilters.endDate) {
          const endDate = new Date(vendorReportFilters.endDate);
          endDate.setHours(23, 59, 59, 999);
          if (transactionDate > endDate) {
            return false;
          }
        }
      }

      return true;
    });

    // Sort by date, latest first
    filtered.sort((a, b) => 
      new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime()
    );

    setVendorReportTransactions(filtered);
  };

  const handleVendorReportDialogOpen = () => {
    setVendorReportDialogOpen(true);
    // Refresh all vendors and users when dialog opens
    fetchAllVendors();
    fetchUsers();
  };

  const handleVendorReportDialogClose = () => {
    setVendorReportDialogOpen(false);
    setVendorReportFilters({
      vendor: null,
      user: null,
      startDate: '',
      endDate: ''
    });
    setVendorReportTransactions([]);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Action Buttons */}
      {isMobile ? (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              onClick={handleActionsMenuOpen}
              sx={{ 
                flex: 1,
                textTransform: 'none',
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Actions
            </Button>
          </Box>
          <Menu
            anchorEl={actionsMenuAnchor}
            open={Boolean(actionsMenuAnchor)}
            onClose={handleActionsMenuClose}
            PaperProps={{
              style: {
                maxHeight: '70vh',
                width: '100%',
                maxWidth: '280px'
              }
            }}
          >
            {hasPermission('add-raw-item') && (
              <MenuItem onClick={() => handleActionSelect('add-raw-item')}>
                <ListItemText primary="Add Raw Item" />
              </MenuItem>
            )}
            {hasPermission('add-raw-item-inventory') && (
              <MenuItem onClick={() => handleActionSelect('add-inventory')}>
                <ListItemText primary="Add Raw Item Inventory" />
              </MenuItem>
            )}
            {hasPermission('view-vendor-inventory-report') && (
              <MenuItem onClick={() => handleActionSelect('vendor-report')}>
                <ListItemText primary="Vendor Inventory Report" />
              </MenuItem>
            )}
            {hasPermission('export-raw-material-pdf') && (
              <MenuItem onClick={() => handleActionSelect('export')}>
                <ListItemText primary="Export to PDF" />
              </MenuItem>
            )}
          </Menu>
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 2 }}>
              {hasPermission('add-raw-item') && (
                <Button
                  variant="contained"
                  onClick={() => setAddRawItemDialogOpen(true)}
                  size="small"
                >
                  Add Raw Item
                </Button>
              )}
              {hasPermission('add-raw-item-inventory') && (
                <Button
                  variant="contained"
                  onClick={() => setAddInventoryDialogOpen(true)}
                  size="small"
                >
                  Add Raw Item Inventory
                </Button>
              )}
              {hasPermission('view-vendor-inventory-report') && (
                <Button
                  variant="contained"
                  onClick={handleVendorReportDialogOpen}
                  size="small"
                >
                  Vendor Inventory Report
                </Button>
              )}
              {hasPermission('export-raw-material-pdf') && (
                <Button 
                  variant="contained" 
                  onClick={exportToPDF}
                  size="small"
                >
                  Export to PDF
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Raw Items Table */}
      {hasPermission('view-raw-material-table') && (
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: 'calc(100vh - 300px)',
          overflowX: 'auto',
          '& .MuiTableCell-root': {
            padding: '8px 16px',
            fontSize: '0.875rem'
          },
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  minWidth: '150px', 
                  maxWidth: '200px',
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  backgroundColor: 'background.paper',
                  borderRight: '2px solid #e0e0e0',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '2px',
                    backgroundColor: '#e0e0e0'
                  }
                }}
              >
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: '120px' }}>
                <TableSortLabel
                  active={orderBy === 'category'}
                  direction={orderBy === 'category' ? order : 'asc'}
                  onClick={() => handleSort('category')}
                >
                  Category
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }}>
                <TableSortLabel
                  active={orderBy === 'quantityUnit'}
                  direction={orderBy === 'quantityUnit' ? order : 'asc'}
                  onClick={() => handleSort('quantityUnit')}
                >
                  Quantity Unit
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '150px' }}>
                <TableSortLabel
                  active={orderBy === 'totalInventoryAdded'}
                  direction={orderBy === 'totalInventoryAdded' ? order : 'asc'}
                  onClick={() => handleSort('totalInventoryAdded')}
                >
                  Total Inventory Added
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '150px' }}>
                <TableSortLabel
                  active={orderBy === 'perUnitPrice'}
                  direction={orderBy === 'perUnitPrice' ? order : 'asc'}
                  onClick={() => handleSort('perUnitPrice')}
                >
                  Per Unit Price
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '150px' }}>
                <TableSortLabel
                  active={orderBy === 'totalValue'}
                  direction={orderBy === 'totalValue' ? order : 'asc'}
                  onClick={() => handleSort('totalValue')}
                >
                  Total Value
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredAndSortedRawItems().map((item) => (
              <TableRow key={item.id}>
                <TableCell 
                  sx={{ 
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    backgroundColor: 'background.paper',
                    borderRight: '2px solid #e0e0e0',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      width: '2px',
                      backgroundColor: '#e0e0e0'
                    }
                  }}
                >
                  {item.name}
                </TableCell>
                <TableCell>{item.category}</TableCell>
                <TableCell>{item.quantityUnit}</TableCell>
                <TableCell align="right">{item.totalInventoryAdded.toFixed(2)}</TableCell>
                <TableCell align="right">PKR {item.perUnitPrice.toFixed(2)}</TableCell>
                <TableCell align="right">PKR {item.totalValue.toFixed(2)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {hasPermission('edit-raw-item') && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setEditingRawItem(item);
                          setEditRawItemDialogOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleShowDetails(item)}
                    >
                      Details
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ fontWeight: 'bold' }}>
              <TableCell 
                sx={{ 
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  backgroundColor: 'background.paper',
                  borderRight: '2px solid #e0e0e0',
                  fontWeight: 'bold',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '2px',
                    backgroundColor: '#e0e0e0'
                  }
                }}
              >
                Total
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell align="right">{calculateTotals(getFilteredAndSortedRawItems()).totalInventoryAdded.toFixed(2)}</TableCell>
              <TableCell></TableCell>
              <TableCell align="right">PKR {calculateTotals(getFilteredAndSortedRawItems()).totalValue.toFixed(2)}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Add Raw Item Dialog */}
      <Dialog open={addRawItemDialogOpen} onClose={() => setAddRawItemDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Raw Item</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={newRawItem.name}
            onChange={(e) => setNewRawItem({ ...newRawItem, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Quantity Unit</InputLabel>
            <Select
              value={newRawItem.quantityUnit}
              label="Quantity Unit"
              onChange={(e) => setNewRawItem({ ...newRawItem, quantityUnit: e.target.value })}
            >
              {getQuantityUnitOptions().map((unit) => (
                <MenuItem key={unit} value={unit}>{unit}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Category"
            fullWidth
            variant="outlined"
            value={newRawItem.category}
            onChange={(e) => setNewRawItem({ ...newRawItem, category: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRawItemDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddRawItem} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Add Raw Item Inventory Dialog */}
      <Dialog open={addInventoryDialogOpen} onClose={() => setAddInventoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Raw Item Inventory</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Raw Item</InputLabel>
            <Select
              value={newInventory.rawItemId}
              label="Raw Item"
              onChange={(e) => setNewInventory({ ...newInventory, rawItemId: e.target.value })}
            >
              {rawItems.map((item) => (
                <MenuItem key={item.id} value={item.id.toString()}>
                  {item.name} ({item.category})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label={`Quantity (${rawItems.find(item => item.id.toString() === newInventory.rawItemId)?.quantityUnit || 'Unit'})`}
            type="number"
            fullWidth
            variant="outlined"
            value={newInventory.quantity}
            onChange={(e) => setNewInventory({ ...newInventory, quantity: e.target.value })}
            inputProps={{ min: 0.01, step: 'any' }}
            error={newInventory.quantity !== '' && !isPositiveNumber(newInventory.quantity)}
            helperText={newInventory.quantity !== '' && !isPositiveNumber(newInventory.quantity) ? 'Must be greater than zero' : ''}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Total Amount (PKR)"
            type="number"
            fullWidth
            variant="outlined"
            value={newInventory.totalAmount}
            onChange={(e) => setNewInventory({ ...newInventory, totalAmount: e.target.value })}
            inputProps={{ min: 0.01, step: 'any' }}
            error={newInventory.totalAmount !== '' && !isPositiveNumber(newInventory.totalAmount)}
            helperText={newInventory.totalAmount !== '' && !isPositiveNumber(newInventory.totalAmount) ? 'Must be greater than zero' : ''}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Transaction Date"
            type="date"
            fullWidth
            variant="outlined"
            value={newInventory.transactionDate}
            onChange={(e) => setNewInventory({ ...newInventory, transactionDate: e.target.value })}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mb: 2 }}
          />
          <Autocomplete
            fullWidth
            options={vendors}
            getOptionLabel={(option) => option.name || ''}
            value={vendors.find(v => v.name === newInventory.vendor) || null}
            onChange={(event, newValue) => {
              setNewInventory({ ...newInventory, vendor: newValue ? newValue.name : '' });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vendor"
                margin="dense"
                variant="outlined"
                required
                error={!newInventory.vendor}
                helperText={!newInventory.vendor ? 'Vendor selection is required' : ''}
              />
            )}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={newInventory.paymentMethod}
              label="Payment Method"
              onChange={(e) => setNewInventory({ ...newInventory, paymentMethod: e.target.value })}
            >
              {PAYMENT_METHOD_OPTIONS.map((method) => (
                <MenuItem key={method} value={method}>{method}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newInventory.notes}
            onChange={(e) => setNewInventory({ ...newInventory, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddInventoryDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddInventory} 
            variant="contained"
            disabled={!isInventoryFormComplete(newInventory)}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Raw Item Inventory Dialog */}
      <Dialog open={editInventoryDialogOpen} onClose={() => setEditInventoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Raw Item Inventory</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Raw Item"
            fullWidth
            variant="outlined"
            value={selectedItemForDetails?.name || editingInventoryEntry?.rawItemName || ''}
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={`Quantity (${selectedItemForDetails?.quantityUnit || 'Unit'})`}
            type="number"
            fullWidth
            variant="outlined"
            value={editInventory.quantity}
            onChange={(e) => setEditInventory({ ...editInventory, quantity: e.target.value })}
            inputProps={{ min: 0.01, step: 'any' }}
            error={editInventory.quantity !== '' && !isPositiveNumber(editInventory.quantity)}
            helperText={editInventory.quantity !== '' && !isPositiveNumber(editInventory.quantity) ? 'Must be greater than zero' : ''}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Total Amount (PKR)"
            type="number"
            fullWidth
            variant="outlined"
            value={editInventory.totalAmount}
            onChange={(e) => setEditInventory({ ...editInventory, totalAmount: e.target.value })}
            inputProps={{ min: 0.01, step: 'any' }}
            error={editInventory.totalAmount !== '' && !isPositiveNumber(editInventory.totalAmount)}
            helperText={editInventory.totalAmount !== '' && !isPositiveNumber(editInventory.totalAmount) ? 'Must be greater than zero' : ''}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Transaction Date"
            type="date"
            fullWidth
            variant="outlined"
            value={editInventory.transactionDate}
            onChange={(e) => setEditInventory({ ...editInventory, transactionDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <Autocomplete
            fullWidth
            options={vendors}
            getOptionLabel={(option) => option.name || ''}
            value={vendors.find(v => v.name === editInventory.vendor) || null}
            onChange={(event, newValue) => {
              setEditInventory({ ...editInventory, vendor: newValue ? newValue.name : '' });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vendor"
                margin="dense"
                variant="outlined"
                required
                error={!editInventory.vendor}
                helperText={!editInventory.vendor ? 'Vendor selection is required' : ''}
              />
            )}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={editInventory.paymentMethod}
              label="Payment Method"
              onChange={(e) => setEditInventory({ ...editInventory, paymentMethod: e.target.value })}
            >
              {PAYMENT_METHOD_OPTIONS.map((method) => (
                <MenuItem key={method} value={method}>{method}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={editInventory.notes}
            onChange={(e) => setEditInventory({ ...editInventory, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditInventoryDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateInventory}
            variant="contained"
            disabled={!isInventoryFormComplete(editInventory)}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Inventory Confirmation Dialog */}
      <Dialog open={deleteInventoryDialogOpen} onClose={() => setDeleteInventoryDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Inventory Entry</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this inventory entry
            {deletingInventoryEntry ? ` (${deletingInventoryEntry.quantity.toFixed(2)} on ${parseTransactionDateToInput(deletingInventoryEntry.transactionDate)})` : ''}?
            This will also remove it from Google Sheets.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteInventoryDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteInventory} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Raw Item Dialog */}
      <Dialog open={editRawItemDialogOpen} onClose={() => setEditRawItemDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Raw Item</DialogTitle>
        <DialogContent>
          {editingRawItem && (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Name"
                fullWidth
                variant="outlined"
                value={editingRawItem.name}
                onChange={(e) => setEditingRawItem({ ...editingRawItem, name: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Category"
                fullWidth
                variant="outlined"
                value={editingRawItem.category}
                onChange={(e) => setEditingRawItem({ ...editingRawItem, category: e.target.value })}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRawItemDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditRawItem} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Inventory Details - {selectedItemForDetails?.name}
        </DialogTitle>
        <DialogContent>
          {selectedItemForDetails && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedItemForDetails.name} ({selectedItemForDetails.category})
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Inventory Added: {selectedItemForDetails.totalInventoryAdded.toFixed(2)} {selectedItemForDetails.quantityUnit}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Value: PKR {selectedItemForDetails.totalValue.toFixed(2)}
              </Typography>
              
              <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Total Amount</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Payment Method</TableCell>
                      <TableCell>Notes</TableCell>
                      {(hasPermission('edit-raw-item-inventory') || hasPermission('delete-raw-item-inventory')) && (
                        <TableCell>Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemInventoryEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          {convertUTCToLocalTime(entry.transactionDate)}
                        </TableCell>
                        <TableCell>{entry.quantity.toFixed(2)} {selectedItemForDetails.quantityUnit}</TableCell>
                        <TableCell>PKR {entry.totalAmount.toFixed(2)}</TableCell>
                        <TableCell>{entry.vendor || '-'}</TableCell>
                        <TableCell>{entry.paymentMethod || '-'}</TableCell>
                        <TableCell>{entry.notes || '-'}</TableCell>
                        {(hasPermission('edit-raw-item-inventory') || hasPermission('delete-raw-item-inventory')) && (
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {hasPermission('edit-raw-item-inventory') && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleOpenEditInventory(entry)}
                                >
                                  Edit
                                </Button>
                              )}
                              {hasPermission('delete-raw-item-inventory') && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => handleOpenDeleteInventory(entry)}
                                >
                                  Delete
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {itemInventoryEntries.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={(hasPermission('edit-raw-item-inventory') || hasPermission('delete-raw-item-inventory')) ? 7 : 6}
                          align="center"
                        >
                          No inventory entries found for the selected date range.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Vendor Inventory Report Dialog */}
      <Dialog open={vendorReportDialogOpen} onClose={handleVendorReportDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>Vendor Inventory Report</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              fullWidth
              options={allVendors}
              getOptionLabel={(option) => option.name || ''}
              value={vendorReportFilters.vendor}
              onChange={(event, newValue) => {
                setVendorReportFilters({ ...vendorReportFilters, vendor: newValue });
                setVendorReportTransactions([]);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vendor"
                  margin="dense"
                  variant="outlined"
                />
              )}
            />
            <Autocomplete
              fullWidth
              options={users}
              getOptionLabel={(option) => option.username || ''}
              value={vendorReportFilters.user}
              onChange={(event, newValue) => {
                setVendorReportFilters({ ...vendorReportFilters, user: newValue });
                setVendorReportTransactions([]);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="User"
                  margin="dense"
                  variant="outlined"
                />
              )}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={vendorReportFilters.startDate}
                onChange={(e) => {
                  setVendorReportFilters({ ...vendorReportFilters, startDate: e.target.value });
                  setVendorReportTransactions([]);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                margin="dense"
                variant="outlined"
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={vendorReportFilters.endDate}
                onChange={(e) => {
                  setVendorReportFilters({ ...vendorReportFilters, endDate: e.target.value });
                  setVendorReportTransactions([]);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                margin="dense"
                variant="outlined"
              />
            </Box>
            <Button
              variant="contained"
              onClick={handleVendorReportSearch}
              disabled={!vendorReportFilters.vendor && !vendorReportFilters.user && !vendorReportFilters.startDate && !vendorReportFilters.endDate}
              sx={{ alignSelf: 'flex-start' }}
            >
              Search
            </Button>
            
            {vendorReportTransactions.length > 0 && (
              <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Raw Item</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell align="right">Total Amount</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Payment Method</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendorReportTransactions.map((entry, index) => {
                      const rawItem = rawItems.find(item => item.id === entry.rawItemId);
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            {convertUTCToLocalTime(entry.transactionDate)}
                          </TableCell>
                          <TableCell>{entry.rawItemName || rawItem?.name || '-'}</TableCell>
                          <TableCell>
                            {entry.quantity.toFixed(2)} {rawItem?.quantityUnit || ''}
                          </TableCell>
                          <TableCell align="right">PKR {entry.totalAmount.toFixed(2)}</TableCell>
                          <TableCell>{entry.userName || '-'}</TableCell>
                          <TableCell>{entry.paymentMethod || '-'}</TableCell>
                          <TableCell>{entry.notes || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow sx={{ fontWeight: 'bold' }}>
                      <TableCell colSpan={3} align="right">
                        <strong>Total Value:</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>PKR {vendorReportTransactions.reduce((sum, t) => sum + t.totalAmount, 0).toFixed(2)}</strong>
                      </TableCell>
                      <TableCell colSpan={3}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {vendorReportTransactions.length === 0 && (vendorReportFilters.vendor || vendorReportFilters.user || vendorReportFilters.startDate || vendorReportFilters.endDate) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                No inventory entries found for the selected filters.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleVendorReportDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RawMaterial;
